import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useBlocker } from 'react-router-dom';
import type { BlockerFunction } from 'react-router-dom';

export type NavigationIntent = 'page' | 'sign-out';

interface NavigationGuard {
  shouldBlock: () => boolean;
  confirmLeave: (intent?: NavigationIntent) => Promise<boolean>;
}

const NavigationGuardContext = createContext<{
  register: (guard: NavigationGuard) => () => void;
  confirmNavigation: (intent?: NavigationIntent) => Promise<boolean>;
} | null>(null);

/** One active editor owns the leave decision; the router preserves the
 * requested destination, including Back/Forward history navigation. */
export const NavigationGuardProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const activeGuard = useRef<NavigationGuard | null>(null);
  const [registeredGuard, setRegisteredGuard] =
    useState<NavigationGuard | null>(null);
  const confirming = useRef(false);
  const confirmationPending = useRef(false);
  const blocker = useBlocker(
    useCallback<BlockerFunction>(
      ({ currentLocation, nextLocation }) =>
        (currentLocation.pathname !== nextLocation.pathname ||
          currentLocation.search !== nextLocation.search) &&
        Boolean(activeGuard.current?.shouldBlock()),
      []
    )
  );
  const latestBlocker = useRef(blocker);
  useLayoutEffect(() => {
    latestBlocker.current = blocker;
  }, [blocker]);

  const register = useCallback((guard: NavigationGuard) => {
    activeGuard.current = guard;
    setRegisteredGuard(guard);
    return () => {
      if (activeGuard.current === guard) {
        activeGuard.current = null;
        setRegisteredGuard(null);
      }
    };
  }, []);

  // Context switchers must ask before clearing the current editor's store.
  // A second action cannot reuse an answer intended for a different action.
  const confirmNavigation = useCallback(
    async (intent?: NavigationIntent): Promise<boolean> => {
      if (confirmationPending.current) return false;
      const guard = activeGuard.current;
      if (!guard?.shouldBlock()) return true;
      confirmationPending.current = true;
      try {
        return await guard.confirmLeave(intent);
      } catch (error) {
        console.error('Failed to confirm navigation:', error);
        return false;
      } finally {
        confirmationPending.current = false;
      }
    },
    []
  );

  useEffect(() => {
    if (blocker.state !== 'blocked' || confirming.current) return;
    confirming.current = true;
    const decide = async () => {
      const leave = await confirmNavigation();
      confirming.current = false;
      // A second navigation while the dialog is open replaces the destination.
      const pending = latestBlocker.current;
      if (pending.state === 'blocked') {
        if (leave) pending.proceed();
        else pending.reset();
      }
    };
    void decide();
  }, [blocker, confirmNavigation]);

  useEffect(() => {
    if (!registeredGuard) return;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (!activeGuard.current?.shouldBlock()) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [registeredGuard]);

  const context = useMemo(
    () => ({ register, confirmNavigation }),
    [register, confirmNavigation]
  );
  return (
    <NavigationGuardContext.Provider value={context}>
      {children}
    </NavigationGuardContext.Provider>
  );
};

export const useNavigationGuard = (
  enabled: boolean,
  guard: NavigationGuard
): void => {
  const register = useContext(NavigationGuardContext)?.register;
  const latestGuard = useRef(guard);
  useLayoutEffect(() => {
    latestGuard.current = guard;
  });
  useLayoutEffect(() => {
    if (!enabled || !register) return;
    return register({
      shouldBlock: () => latestGuard.current.shouldBlock(),
      confirmLeave: (intent) => latestGuard.current.confirmLeave(intent),
    });
  }, [enabled, register]);
};

const allowNavigation = async () => true;

export const useConfirmNavigation = (): ((
  intent?: NavigationIntent
) => Promise<boolean>) =>
  useContext(NavigationGuardContext)?.confirmNavigation ?? allowNavigation;
